/* eslint-disable max-len */
import { Dispatch, MouseEvent, SetStateAction, useState } from "react";
import {
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
  useUpdate
} from "react-admin";
import { useNavigate, useParams } from "react-router-dom";
import { muiColors } from "themes/colors";
import dayjs from "dayjs";
import {
  CancelledIcon,
  CheckIcon,
  RefundedOrderButtonIcon,
  ScheduleIcon
} from "assets/Icons";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { ListItemButton, Popover } from "@mui/material";
import * as User from "providers/user";
import * as Axios from "../providers/axios";
import { CustomButton } from "./CustomButton";
import { getZonedDate } from "../utils/helpers";

const AppointmentDetailsCustomButtons = ({
  setCancelModalAppointmentId,
  rescheduledOrderId
}: {
  setCancelModalAppointmentId: Dispatch<SetStateAction<string | null>>;
  rescheduledOrderId?: string;
}) => {
  const translate = useTranslate();
  const { permissions } = User.useUser();
  const record = useRecordContext();
  const [edit] = useUpdate();
  const { api } = Axios.useAxios();
  const { id } = useParams<{ id: string }>();
  const refresh = useRefresh();
  const notify = useNotify();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleAction = (fieldName: string, fieldValue: string) => {
    const updatedAppointment = {
      id,
      data: {
        [fieldName]: fieldValue
      }
    };

    edit("Appointment", updatedAppointment, {
      onSuccess: () => {
        refresh();
      },
      onError: () => {
        // eslint-disable-next-line no-console
        console.error(`Error occurred during ${fieldName} action.`);
      }
    });
  };

  const handleImpersonate = async (userId: string, appointmentId: string) => {
    try {
      await api.post("v1/users/impersonate-login", { userId });

      window.location.href = `${process.env.REACT_APP_CUSTOMER_APP_URL}/me/booking/${appointmentId}`;
    } catch (error) {
      notify(error.response.data.message, {
        type: "error",
        undoable: false,
        anchorOrigin: { vertical: "top", horizontal: "center" }
      });
    }
  };

  const handleClosePopover = (e: any): void => {
    setAnchorEl(null);
  };

  if (!record) {
    return null;
  }

  const isPopoverOpen = Boolean(anchorEl);
  const popoverId = isPopoverOpen ? "appointment-details-more" : undefined;

  const isCheckedIn =
    record.checked_in_at && dayjs(record.checked_in_at).isAfter("2023-07-12");

  const isCheckedOut = record?.Order?.payment_status === "FULLY_PAID";

  const currentTime = getZonedDate(
    new Date().toISOString(),
    record.location.timezone
  ).getTime();

  const appointmentStartTime = getZonedDate(
    record.start_time,
    record.location.timezone
  ).getTime();

  const isPastAppointment = currentTime > appointmentStartTime;

  return (
    <div
      className="flex gap-3 sm:gap-2 flex-wrap w-full sm:w-auto"
      data-cy="check-in-container"
    >
      {record.rescheduled_at && !isCheckedOut && (
        <CustomButton
          text={translate("appointment.rescheduled")}
          variant="secondary"
          sx={{
            border: `dashed 1px ${muiColors.button.disabled}`,
            "&:disabled": {
              color: "#af6ef5",
              svg: {
                width: "17px",
                fill: "#af6ef5",
                fillOpacity: 1,
                path: {
                  fill: "#af6ef5",
                  fillOpacity: 1
                }
              }
            },
            "@media screen and (max-width:640px)": {
              width: "100%"
            }
          }}
          startIcon={<ScheduleIcon />}
          disabled={true}
        />
      )}
      {!isPastAppointment &&
        !record.cancelled_at &&
        !permissions.isCompanyLimited &&
        !permissions.isCompanyBasic &&
        !permissions.isArtist && (
          <CustomButton
            text={translate("appointment.reschedule")}
            variant="primary"
            cyId="reschedule-appointment-button"
            onClick={() =>
              handleImpersonate(record.user.id, record.id as string)
            }
            sx={{
              "@media screen and (max-width:640px)": {
                width: "100%"
              }
            }}
          />
        )}
      {record.cancelled_at && !isCheckedOut && !record.rescheduled_at && (
        <CustomButton
          text={translate("appointment.cancelled")}
          variant="secondary"
          sx={{
            border: `dashed 1px ${muiColors.button.disabled}`,
            "&:disabled": {
              color: muiColors.error,
              svg: {
                fill: muiColors.error,
                fillOpacity: 1,
                path: {
                  fill: muiColors.error,
                  fillOpacity: 1
                }
              }
            },
            "@media screen and (max-width:640px)": {
              width: "100%"
            }
          }}
          startIcon={<CancelledIcon />}
          disabled={true}
        />
      )}
      {isCheckedOut && (
        <CustomButton
          text={translate("appointment.checked_out")}
          variant="secondary"
          startIcon={<CheckIcon />}
          disabled={true}
          sx={{
            "@media screen and (max-width:640px)": {
              width: "100%"
            }
          }}
        />
      )}
      {!isCheckedIn && !record.cancelled_at && !isCheckedOut && (
        <CustomButton
          text={translate("appointment.check_in")}
          cyId="check-in-button"
          variant="secondary"
          onClick={() =>
            handleAction("checked_in_at", new Date().toISOString())
          }
          sx={{
            "@media screen and (max-width:640px)": {
              width: "100%"
            }
          }}
        />
      )}
      {!permissions.isCompanyLimited &&
        !permissions.isCompanyBasic &&
        isCheckedIn &&
        !record.cancelled_at &&
        !isCheckedOut && (
          <CustomButton
            text={translate("appointment.check_out")}
            disabled={!record?.Order?.id}
            onClick={() =>
              navigate(`/dashboard/Appointment-order/${record?.Order?.id}/show`)
            }
            sx={{
              "@media screen and (max-width:640px)": {
                width: "100%"
              }
            }}
          />
        )}
      {record?.Order?.payment_status === "REFUNDED" && (
        <CustomButton
          text={translate("order.refunded")}
          variant="secondary"
          sx={{
            border: `dashed 1px ${muiColors.button.disabled}`,
            "&:disabled": {
              color: muiColors.warning,
              svg: {
                fill: muiColors.warning,
                fillOpacity: 1,
                path: {
                  fill: muiColors.warning,
                  fillOpacity: 1
                }
              }
            }
          }}
          startIcon={<RefundedOrderButtonIcon />}
          disabled={true}
          className="w-full sm:w-auto"
        />
      )}
      <CustomButton
        onClick={(event: MouseEvent<HTMLButtonElement>): void => {
          setAnchorEl(event?.currentTarget);
        }}
        cyId="appointment_three_dots_button"
        text={translate("common.more")}
        variant="secondary"
        startIcon={<MoreHorizIcon />}
        sx={{
          "@media screen and (max-width:640px)": {
            width: "100%"
          }
        }}
      />
      <Popover
        id={popoverId}
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        elevation={4}
        sx={{
          "& .MuiPopover-paper": {
            backgroundColor: "#fff",
            borderRadius: "4px"
          }
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: -8,
          horizontal: "right"
        }}
      >
        {!permissions.isCompanyLimited && !permissions.isCompanyBasic && (
          <ListItemButton
            onClick={() =>
              navigate(
                `/dashboard/Appointment-order/${
                  record?.Order?.id || rescheduledOrderId
                }/show`
              )
            }
            sx={{
              fontSize: "14px",
              fontFamily: "Inter",
              width: "164px",
              "& .MuiListItemButton-root": {
                fontSize: "14px",
                fontFamily: "Inter"
              }
            }}
          >
            {translate("appointment.order_details")}
          </ListItemButton>
        )}
        {/* <ListItemButton
          sx={{
            fontSize: "14px",
            fontFamily: "Inter",
            width: "164px",
            "& .MuiListItemButton-root": {
              fontSize: "14px",
              fontFamily: "Inter"
            }
          }}
        >
          {translate("appointment.reschedule")}
        </ListItemButton> */}
        {!record.cancelled_at &&
          record?.Order?.payment_status !== "REFUNDED" && (
            <ListItemButton
              onClick={(e) => {
                // handleAction("cancelled_at", new Date().toISOString());
                setCancelModalAppointmentId(id || null);
                handleClosePopover(e);
              }}
              data-cy="cancel-appointment-button"
              sx={{
                fontSize: "14px",
                fontFamily: "Inter",
                color: muiColors.error,
                "& .MuiListItemButton-root": {
                  fontSize: "14px",
                  fontFamily: "Inter"
                }
              }}
            >
              {translate("appointment.cancel")}
            </ListItemButton>
          )}
      </Popover>
    </div>
  );
};

export default AppointmentDetailsCustomButtons;
